import { useQuery } from '@tanstack/react-query'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { api, type ChatListItem } from '../lib/api'
import { getPermission, showNotification } from '../lib/notifications'

// Headless component — mounts once in __root, watches the same ['chats']
// query the sidebar already uses, and fires a Web Notification whenever
// a new message arrives that wasn't sent by the viewer themselves and
// the viewer isn't currently looking at that specific chat.
//
// State model: a ref-held Map<chatId, lastSeenMessageId>. We compare
// the current poll's lastMessage.id against what we last saw; mismatch
// means there's a fresh message. We deliberately skip the very first
// hydration (initialized=false) so the user doesn't get notification
// spam for every backlog message right after login.
export function ChatNotifications() {
  const navigate = useNavigate()
  const location = useRouterState({ select: (s) => s.location })
  const { data: me } = useQuery({ queryKey: ['auth', 'me'], queryFn: () => api.me() })
  const myId = me?.user?.id ?? null

  // Same query the sidebar uses — React Query dedupes across consumers.
  const { data: chatsData } = useQuery({
    queryKey: ['chats'],
    queryFn: () => api.listChats(),
    enabled: !!myId,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  })

  const lastSeenRef = useRef<Map<string, string>>(new Map())
  const initializedRef = useRef(false)

  // Two notification-click sources both route through this listener:
  //   - lib/notifications.ts (in-page Notification, active-tab path)
  //     dispatches `embertime:notification-click`
  //   - public/sw.js (push event → OS notification → click → focused
  //     SPA tab) sends a `postMessage({ type: 'navigate', url })`
  //
  // Lives here because we have a router-aware `navigate` handy.
  useEffect(() => {
    const onCustom = (e: Event) => {
      const detail = (e as CustomEvent<{ to: string }>).detail
      if (detail?.to) navigate({ to: detail.to as string })
    }
    const onSwMessage = (e: MessageEvent) => {
      const data = e.data
      if (
        data &&
        typeof data === 'object' &&
        data.type === 'navigate' &&
        typeof data.url === 'string'
      ) {
        navigate({ to: data.url })
      }
    }
    window.addEventListener('embertime:notification-click', onCustom)
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', onSwMessage)
    }
    return () => {
      window.removeEventListener('embertime:notification-click', onCustom)
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', onSwMessage)
      }
    }
  }, [navigate])

  useEffect(() => {
    if (!myId) return
    const chats = chatsData?.chats
    if (!chats) return

    // First poll after mount/login: just snapshot, don't notify.
    if (!initializedRef.current) {
      for (const c of chats) {
        if (c.lastMessage) lastSeenRef.current.set(c.id, c.lastMessage.id)
      }
      initializedRef.current = true
      return
    }

    if (getPermission() !== 'granted') return
    const activeChatId = activeChatIdFromPath(location.pathname, location.search)
    const docHidden = typeof document !== 'undefined' && document.hidden

    for (const c of chats) {
      if (!c.lastMessage) continue
      const last = c.lastMessage
      const seen = lastSeenRef.current.get(c.id)
      if (seen === last.id) continue // already seen

      // Snapshot before deciding to notify — even if we skip, we don't
      // want to re-notify on the next tick.
      lastSeenRef.current.set(c.id, last.id)

      // Don't notify about my own messages.
      if (last.senderUserId === myId) continue
      // Don't notify if I'm already actively viewing this chat AND
      // the tab is focused. (Tab in background → still notify.)
      if (!docHidden && activeChatId === c.id) continue

      const senderName = c.members.find((m) => m.id === last.senderUserId)?.name ?? 'Jemand'
      const chatLabel = chatLabelFor(c, myId)
      const title = c.isGroup ? `${senderName} · ${chatLabel}` : senderName
      const time = new Date(last.createdAt).toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit',
      })
      const kindPreview =
        last.kind === 'voice'
          ? '🎤 Sprachnachricht'
          : last.kind === 'file'
            ? '📎 Datei'
            : last.kind === 'link'
              ? '🔗 Link'
              : ''
      const body = `${time} · ${truncate(last.content || kindPreview, 180)}`
      showNotification({
        title,
        body,
        tag: `chat-${c.id}`,
        navigateTo: `/chat?id=${c.id}`,
      })
    }
  }, [chatsData, myId, location.pathname, location.search])

  return null
}

function activeChatIdFromPath(pathname: string, search: unknown): string | null {
  if (pathname !== '/chat') return null
  if (typeof search === 'object' && search && 'id' in search) {
    const v = (search as { id?: unknown }).id
    return typeof v === 'string' ? v : null
  }
  return null
}

function chatLabelFor(chat: ChatListItem, myId: string): string {
  if (chat.isGroup) return chat.name ?? 'Gruppe'
  const other = chat.members.find((m) => m.id !== myId)
  return other?.name ?? 'Chat'
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s
  return `${s.slice(0, max - 1).trimEnd()}…`
}
